/**
 * YoutubeLite Component
 *
 * Performance-optimized YouTube embed using facade pattern.
 * Only loads YouTube iframe and scripts on user interaction.
 *
 * Performance benefits:
 * - Loads thumbnail image instead of full iframe initially (~100KB vs ~500KB+)
 * - Defers all YouTube scripts until user clicks play
 * - Uses IntersectionObserver for lazy loading
 * - Improves LCP, TTI, and overall page performance
 */

"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface YoutubeLiteProps {
	/** YouTube video ID (e.g., 'dQw4w9WgXcQ') */
	videoId: string;
	/** Video title for accessibility */
	title?: string;
	/** Custom thumbnail image URL (optional, uses YouTube default if not provided) */
	thumbnail?: string;
	/** Quality of YouTube thumbnail: default, mqdefault, hqdefault, sddefault, maxresdefault */
	thumbnailQuality?:
		| "default"
		| "mqdefault"
		| "hqdefault"
		| "sddefault"
		| "maxresdefault";
	/** Automatically load iframe when scrolled into view */
	autoLoad?: boolean;
	/** Additional YouTube player parameters */
	params?: string;
	/** Custom play button label */
	playLabel?: string;
	/** Additional CSS classes */
	className?: string;
}

/**
 * Get YouTube thumbnail URL for a video
 */
function getThumbnailUrl(
	videoId: string,
	quality: YoutubeLiteProps["thumbnailQuality"] = "hqdefault",
): string {
	return `https://i.ytimg.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * Get YouTube webp thumbnail URL for better performance
 */
function getWebpThumbnailUrl(
	videoId: string,
	quality: YoutubeLiteProps["thumbnailQuality"] = "hqdefault",
): string {
	return `https://i.ytimg.com/vi_webp/${videoId}/${quality}.webp`;
}

export function YoutubeLite({
	videoId,
	title = "YouTube video",
	thumbnail,
	thumbnailQuality = "hqdefault",
	autoLoad = false,
	params = "autoplay=1",
	playLabel = "Play",
	className,
}: YoutubeLiteProps) {
	const [isActivated, setIsActivated] = useState(false);
	const [isIntersecting, setIsIntersecting] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	// IntersectionObserver for autoLoad feature
	useEffect(() => {
		if (!autoLoad || !containerRef.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (entry?.isIntersecting) {
					setIsIntersecting(true);
				}
			},
			{
				rootMargin: "0px",
				threshold: 0,
			},
		);

		observer.observe(containerRef.current);

		return () => {
			observer.disconnect();
		};
	}, [autoLoad]);

	// Auto-activate when intersecting (if autoLoad enabled)
	useEffect(() => {
		if (autoLoad && isIntersecting && !isActivated) {
			setIsActivated(true);
		}
	}, [autoLoad, isIntersecting, isActivated]);

	const handleActivate = () => {
		setIsActivated(true);
	};

	const thumbnailUrl = thumbnail || getThumbnailUrl(videoId, thumbnailQuality);
	const webpThumbnailUrl = getWebpThumbnailUrl(videoId, thumbnailQuality);
	const iframeSrc = `https://www.youtube.com/embed/${videoId}?${params}`;

	return (
		// biome-ignore lint/a11y/useAriaPropsSupportedByRole: role="button" is conditionally set with aria-label when !isActivated
		// biome-ignore lint/a11y/noStaticElementInteractions: div conditionally acts as interactive button for facade pattern
		<div
			ref={containerRef}
			className={cn(
				"relative aspect-video w-full overflow-hidden rounded-lg bg-black",
				!isActivated && "cursor-pointer",
				className,
			)}
			role={!isActivated ? "button" : undefined}
			aria-label={!isActivated ? `${playLabel}: ${title}` : undefined}
			tabIndex={!isActivated ? 0 : undefined}
			onClick={!isActivated ? handleActivate : undefined}
			onKeyDown={
				!isActivated
					? (e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								handleActivate();
							}
						}
					: undefined
			}
		>
			{/* Facade: Show thumbnail and play button before activation */}
			{!isActivated && (
				<>
					{/* Thumbnail Image */}
					<picture>
						{/* WebP for better performance */}
						<source srcSet={webpThumbnailUrl} type="image/webp" />
						{/* Fallback to JPEG */}
						<img
							src={thumbnailUrl}
							alt={title}
							className="absolute inset-0 size-full object-cover"
							loading="lazy"
							decoding="async"
						/>
					</picture>

					{/* Play Button */}
					<div className="absolute inset-0 flex items-center justify-center">
						<div
							className={cn(
								"relative flex size-16 items-center justify-center rounded-full",
								"bg-destructive shadow-lg transition-all duration-200",
								"hover:scale-110 hover:bg-destructive/90",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2",
							)}
							aria-hidden="true"
						>
							{/* Play Icon - YouTube style triangle */}
							<svg
								className="ml-1 size-8 text-destructive-foreground"
								viewBox="0 0 24 24"
								fill="currentColor"
								xmlns="http://www.w3.org/2000/svg"
								aria-hidden="true"
							>
								<path d="M8 5v14l11-7z" />
							</svg>
						</div>
					</div>

					{/* Screen reader text */}
					<span className="sr-only">
						{playLabel}: {title}
					</span>
				</>
			)}

			{/* Real iframe: Load only after activation */}
			{isActivated && (
				<iframe
					src={iframeSrc}
					title={title}
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					referrerPolicy="strict-origin-when-cross-origin"
					allowFullScreen
					className="absolute inset-0 size-full border-0"
				/>
			)}
		</div>
	);
}
